import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Trash2 } from 'lucide-react'
import type { MetricSheetState } from '../../../../../../services/projectAutofillDraftService.ts'
import {
  addMetricSheetRow,
  applyMetricSheetCellChange,
  columnHeaderLabel,
  isMetricSheetTemplateSupported,
  MAX_METRIC_SHEET_ROWS,
  removeMetricSheetRow
} from '../../../../../../utils/conversationalMode/metricSheetStateUtils.ts'
import styles from '../BudgetSheetEditor/BudgetSheetEditor.module.scss'

const METRIC_SHEET_TOAST_SHOW_DEBOUNCE_MS = 900
const METRIC_SHEET_TOAST_VISIBLE_MS = 2200

function cellToInputString(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return String(v)
  return String(v)
}

function isNumericCol(id: string): boolean {
  return id === 'quantity'
}

function colWidthClassName(id: string): string {
  switch (id) {
    case 'item':
      return styles.colItem
    case 'unit':
      return styles.colUnit
    case 'quantity':
      return styles.colQuantity
    case 'notes':
      return styles.colNotes
    default:
      return styles.colFlex
  }
}

type MetricSheetItemTextareaProps = {
  className: string
  value: string
  disabled: boolean
  onChange: (v: string) => void
}

const MetricSheetItemTextarea: React.FC<MetricSheetItemTextareaProps> = ({
  className,
  value,
  disabled,
  onChange
}) => {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let rafId = 0
    const apply = () => {
      const node = ref.current
      if (!node) return
      node.style.height = 'auto'
      node.style.height = `${node.scrollHeight}px`
    }
    const schedule = () => {
      if (rafId !== 0) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = 0
        apply()
      })
    }
    schedule()
    const ro = new ResizeObserver(schedule)
    const par = el.parentElement
    if (par) ro.observe(par)
    return () => {
      ro.disconnect()
      if (rafId !== 0) cancelAnimationFrame(rafId)
    }
  }, [value])
  return (
    <textarea
      ref={ref}
      rows={1}
      className={className}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

type Props = {
  state: MetricSheetState
  onChange: (next: MetricSheetState) => void
  disabled: boolean
  showHeading?: boolean
  flushLayout?: boolean
  dockInModal?: boolean
  onIdleCommit?: () => void
}

const MetricSheetEditor: React.FC<Props> = ({
  state,
  onChange,
  disabled,
  showHeading = true,
  flushLayout = false,
  dockInModal = false,
  onIdleCommit
}) => {
  const [draftAppliedVisible, setDraftAppliedVisible] = useState(false)
  const showDraftToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideDraftToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const draftToastActiveRef = useRef(false)
  const supported = isMetricSheetTemplateSupported(state)
  const readOnly = disabled || !supported
  const bannerText = !supported
    ? 'Esta planilla usa una versión de plantilla que esta aplicación aún no puede editar.'
    : null

  const commitChange = useCallback(
    (next: MetricSheetState) => {
      onChange(next)
      if (!dockInModal || readOnly) return

      if (showDraftToastTimerRef.current) {
        clearTimeout(showDraftToastTimerRef.current)
        showDraftToastTimerRef.current = null
      }

      const armHideDraftToast = () => {
        if (hideDraftToastTimerRef.current) clearTimeout(hideDraftToastTimerRef.current)
        hideDraftToastTimerRef.current = setTimeout(() => {
          draftToastActiveRef.current = false
          setDraftAppliedVisible(false)
          hideDraftToastTimerRef.current = null
        }, METRIC_SHEET_TOAST_VISIBLE_MS)
      }

      if (draftToastActiveRef.current) {
        armHideDraftToast()
        return
      }

      showDraftToastTimerRef.current = setTimeout(() => {
        showDraftToastTimerRef.current = null
        draftToastActiveRef.current = true
        setDraftAppliedVisible(true)
        onIdleCommit?.()
        armHideDraftToast()
      }, METRIC_SHEET_TOAST_SHOW_DEBOUNCE_MS)
    },
    [onChange, dockInModal, readOnly, onIdleCommit]
  )

  useEffect(() => {
    return () => {
      if (showDraftToastTimerRef.current) clearTimeout(showDraftToastTimerRef.current)
      if (hideDraftToastTimerRef.current) clearTimeout(hideDraftToastTimerRef.current)
      draftToastActiveRef.current = false
    }
  }, [])

  const canAddRow = !readOnly && state.rows.length < MAX_METRIC_SHEET_ROWS

  const patchCell = (rowId: string, colId: string, raw: string) => {
    commitChange(applyMetricSheetCellChange(state, rowId, colId, raw))
  }

  const handleAddRow = () => {
    commitChange(addMetricSheetRow(state))
  }

  const handleRemoveRow = (rowId: string) => {
    commitChange(removeMetricSheetRow(state, rowId))
  }

  const renderCellInput = (rowId: string, colId: string, className: string) => {
    const row = state.rows.find((r) => r.rowId === rowId)
    const raw = cellToInputString(row?.cells?.[colId])
    if (colId === 'item' || colId === 'notes') {
      return (
        <MetricSheetItemTextarea
          className={`${className} ${colId === 'item' ? styles.inputItem : `${styles.inputTextarea} ${styles.inputNotes}`}`}
          value={raw}
          disabled={readOnly}
          onChange={(v) => patchCell(rowId, colId, v)}
        />
      )
    }
    return (
      <input
        type="text"
        className={className}
        value={raw}
        disabled={readOnly}
        inputMode={isNumericCol(colId) ? 'decimal' : undefined}
        onChange={(e) => patchCell(rowId, colId, e.target.value)}
      />
    )
  }

  const thClassForCol = (colId: string) => {
    const base = isNumericCol(colId) ? styles.thNum : styles.th
    if (colId === 'item' || colId === 'notes') return `${base} ${styles.thTall}`
    return `${base} ${styles.thNarrow}`
  }

  const tdClassForCol = (colId: string) => {
    const base = isNumericCol(colId) ? styles.tdNum : styles.td
    if (colId === 'item' || colId === 'notes') return `${base} ${styles.tdTall}`
    return `${base} ${styles.tdNarrow}`
  }

  const colIndex = (id: string) => state.columns.findIndex((c) => c.id === id)
  const hasCol = (id: string) => colIndex(id) >= 0

  return (
    <div
      className={[
        styles.wrap,
        !showHeading ? styles.wrapNoHeading : '',
        flushLayout ? styles.wrapFlush : '',
        dockInModal ? styles.wrapDockInModal : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showHeading && <h4 className={styles.title}>Cómputo métrico</h4>}
      {bannerText ? (
        <div className={[styles.banner, dockInModal ? styles.bannerDocked : ''].filter(Boolean).join(' ')}>
          {bannerText}
        </div>
      ) : null}
      <div className={[styles.sheetFrame, dockInModal ? styles.sheetFrameDocked : ''].filter(Boolean).join(' ')}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <button type="button" className={styles.toolBtn} disabled={!canAddRow} onClick={handleAddRow}>
              Agregar fila
            </button>
          </div>
        </div>

        <div
          className={[styles.desktopTable, dockInModal ? styles.desktopTableDocked : ''].filter(Boolean).join(' ')}
        >
          <div
            className={[styles.tableScroll, dockInModal ? styles.tableScrollDocked : ''].filter(Boolean).join(' ')}
          >
            <table className={`${styles.table} ${styles.tableFixed}`}>
              <colgroup>
                <col className={styles.colRowNum} />
                {state.columns.map((col) => (
                  <col key={col.id} className={colWidthClassName(col.id)} />
                ))}
                <col className={styles.colActions} />
              </colgroup>
              <thead>
                <tr>
                  <th className={styles.thRowNum} scope="col">
                    N.º
                  </th>
                  {state.columns.map((col) => (
                    <th key={col.id} className={thClassForCol(col.id)} scope="col">
                      {columnHeaderLabel(col)}
                    </th>
                  ))}
                  <th className={styles.thActions} aria-label="Acciones por fila" scope="col" />
                </tr>
              </thead>
              <tbody>
                {state.rows.length === 0 && (
                  <tr>
                    <td colSpan={state.columns.length + 2} className={styles.tdEmpty}>
                      <span className={styles.emptyHint}>
                        Todavía no hay filas en el cómputo. Usá «Agregar fila».
                      </span>
                    </td>
                  </tr>
                )}
                {state.rows.map((row, idx) => (
                  <tr key={row.rowId}>
                    <td className={styles.tdRowNum}>{idx + 1}</td>
                    {state.columns.map((col) => (
                      <td key={col.id} className={tdClassForCol(col.id)}>
                        {renderCellInput(
                          row.rowId,
                          col.id,
                          isNumericCol(col.id) ? styles.inputNum : styles.input
                        )}
                      </td>
                    ))}
                    <td className={styles.rowActions}>
                      <button
                        type="button"
                        className={styles.removeRow}
                        disabled={readOnly || state.rows.length <= 1}
                        title="Quitar esta fila"
                        aria-label="Quitar esta fila"
                        onClick={() => handleRemoveRow(row.rowId)}
                      >
                        <Trash2 size={18} strokeWidth={2} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={[styles.mobileList, dockInModal ? styles.mobileListDocked : ''].filter(Boolean).join(' ')}>
          {state.rows.length === 0 && (
            <p className={styles.emptyHint}>Todavía no hay filas en el cómputo. Usá «Agregar fila».</p>
          )}
          {state.rows.map((row, idx) => (
            <div key={row.rowId} className={styles.card}>
              <div className={styles.cardRowNum}>Fila {idx + 1}</div>
              {hasCol('item') && (
                <>
                  <div className={styles.cardItemLabel}>
                    {columnHeaderLabel(state.columns[colIndex('item')]!)}
                  </div>
                  <MetricSheetItemTextarea
                    className={`${styles.cardItemInput} ${styles.inputItem}`}
                    value={cellToInputString(row.cells?.item)}
                    disabled={readOnly}
                    onChange={(v) => patchCell(row.rowId, 'item', v)}
                  />
                </>
              )}
              <div className={styles.grid2}>
                {hasCol('unit') && (
                  <div>
                    <label className={styles.fieldLabel}>
                      {columnHeaderLabel(state.columns[colIndex('unit')]!)}
                    </label>
                    {renderCellInput(row.rowId, 'unit', styles.cardItemInput)}
                  </div>
                )}
                {hasCol('quantity') && (
                  <div>
                    <label className={styles.fieldLabel}>
                      {columnHeaderLabel(state.columns[colIndex('quantity')]!)}
                    </label>
                    {renderCellInput(row.rowId, 'quantity', styles.cardItemInput)}
                  </div>
                )}
              </div>
              {hasCol('notes') && (
                <div>
                  <label className={styles.fieldLabel}>
                    {columnHeaderLabel(state.columns[colIndex('notes')]!)}
                  </label>
                  <MetricSheetItemTextarea
                    className={`${styles.cardItemInput} ${styles.inputTextarea} ${styles.inputNotes}`}
                    value={cellToInputString(row.cells?.notes)}
                    disabled={readOnly}
                    onChange={(v) => patchCell(row.rowId, 'notes', v)}
                  />
                </div>
              )}
              <div className={styles.cardFooter}>
                <button
                  type="button"
                  className={styles.cardRemove}
                  disabled={readOnly || state.rows.length <= 1}
                  onClick={() => handleRemoveRow(row.rowId)}
                >
                  Quitar fila
                </button>
              </div>
            </div>
          ))}
        </div>
        {dockInModal && !readOnly && draftAppliedVisible ? (
          <div className={styles.draftAppliedToast} role="status" aria-live="polite">
            Cómputo actualizado
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default MetricSheetEditor
